/**
 * AppEditModal (Lote F2 — Sub-projeto 5).
 *
 * Modal de edição de campos seguros de app ZTNA. 5 campos editáveis:
 * - display_name: texto livre
 * - upstream_url: URL backend (validação circular contra app.hostname)
 * - identity_mode: radio (header/oidc/mtls)
 * - oidc_redirect_uris: TagsInput
 * - access_policy: JSON editor (Textarea controlled)
 *
 * Validações inline + warning banner. Backend valida tudo de novo
 * (defense in depth). Submit via tanstack-query useMutation.
 */
import { useState } from 'react'
import {
  Alert,
  Button,
  Group,
  Modal,
  Radio,
  Stack,
  Switch,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ztnaApi } from '../api'
import { foiParaGmud, notifyPendingGmud } from '../gmudNotice'
import type {
  AppHealthSummary,
  AppUpdateRequest,
  TimeWindow,
} from '../types'
import { TimeWindowFieldset } from '../Wizard/Step3Routing'
import { formatApiError } from '@/api/errors'

interface Props {
  app: AppHealthSummary
  opened: boolean
  onClose: () => void
}

function parseHostname(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase()
  } catch {
    return null
  }
}

const FQDN_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/

export function AppEditModal({ app, opened, onClose }: Props) {
  const queryClient = useQueryClient()

  const [displayName, setDisplayName] = useState(app.display_name)
  const [hostname, setHostname] = useState(app.hostname)
  const [upstreamUrl, setUpstreamUrl] = useState(app.upstream_url ?? '')
  const [identityMode, setIdentityMode] = useState<'header' | 'oidc' | 'mtls'>(
    (app.identity_mode as 'header' | 'oidc' | 'mtls') ?? 'header',
  )
  const [redirectUris, setRedirectUris] = useState<string[]>(
    app.oidc_redirect_uris ?? [],
  )
  const [accessPolicyJson, setAccessPolicyJson] = useState<string>(
    JSON.stringify(app.access_policy ?? {}, null, 2),
  )
  const initialPolicy = (app.access_policy ?? {}) as Record<string, unknown>
  const [allowedHours, setAllowedHours] = useState<TimeWindow | null>(
    (initialPolicy.allowed_hours as TimeWindow | null) ?? null,
  )
  const [bypassRoles, setBypassRoles] = useState<string[]>(
    (initialPolicy.bypass_time_window_roles as string[] | undefined) ?? [
      'admin',
      'manager',
      'gestor',
    ],
  )
  const [isPrivate, setIsPrivate] = useState<boolean>(app.is_private ?? false)

  const hostnameTrimmed = hostname.trim().toLowerCase()
  const hostnameChanged = hostnameTrimmed !== app.hostname.toLowerCase()
  const hostnameError =
    hostnameChanged && !FQDN_RE.test(hostnameTrimmed)
      ? 'Hostname deve ser FQDN valido (ex: portal.tenant.com.br)'
      : null

  const upstreamCircular = (() => {
    const upstreamHost = parseHostname(upstreamUrl)
    // Anti-loop checa contra o hostname target (novo se mudou, atual senao).
    const targetHost = hostnameTrimmed || app.hostname.toLowerCase()
    return upstreamHost != null && upstreamHost === targetHost
  })()

  const accessPolicyError = (() => {
    if (!accessPolicyJson.trim()) return null
    try {
      JSON.parse(accessPolicyJson)
      return null
    } catch (e) {
      return (e as Error).message
    }
  })()

  const updateMutation = useMutation({
    mutationFn: (patch: AppUpdateRequest) => ztnaApi.updateApp(app.id, patch),
    onSuccess: (response) => {
      // No ramo da GMUD o backend devolve `fields_changed: []`, e a mensagem
      // sairia como "Campos alterados: " — vazia e afirmando que houve edicao.
      // Note que o gate e' POR CAMPO: PATCH so com `display_name` nao passa por
      // aqui, porque nao exige aprovacao.
      if (foiParaGmud(response)) {
        notifyPendingGmud('a edição', app.display_name || app.slug, response.gmud_id)
        onClose()
        return
      }
      queryClient.invalidateQueries({ queryKey: ['ztna', 'apps'] })
      queryClient.invalidateQueries({
        queryKey: ['ztna', 'app-audit-events', app.id],
      })
      notifications.show({
        title: 'App atualizada',
        message: `Campos alterados: ${response.fields_changed.join(', ')}`,
        color: 'teal',
      })
      onClose()
    },
    onError: (err: unknown) => {
      notifications.show({
        title: 'Falha ao atualizar app',
        message: formatApiError(err, 'Falha ao atualizar a app.'),
        color: 'red',
      })
    },
  })

  function handleSubmit() {
    if (upstreamCircular || accessPolicyError || hostnameError) return
    const patch: AppUpdateRequest = {}
    if (displayName !== app.display_name) patch.display_name = displayName
    if (hostnameChanged) patch.hostname = hostnameTrimmed
    if (isPrivate !== (app.is_private ?? false)) patch.is_private = isPrivate
    if ((upstreamUrl || '') !== (app.upstream_url ?? '')) {
      patch.upstream_url = upstreamUrl
    }
    if (identityMode !== app.identity_mode) patch.identity_mode = identityMode
    const sortedNew = [...redirectUris].sort().join('|')
    const sortedOld = [...(app.oidc_redirect_uris ?? [])].sort().join('|')
    if (sortedNew !== sortedOld) patch.oidc_redirect_uris = redirectUris
    try {
      const parsed = JSON.parse(accessPolicyJson || '{}')
      if (allowedHours) {
        parsed.allowed_hours = allowedHours
        parsed.bypass_time_window_roles = bypassRoles
      } else {
        delete parsed.allowed_hours
        delete parsed.bypass_time_window_roles
      }
      const oldPolicy = JSON.stringify(app.access_policy ?? {})
      if (JSON.stringify(parsed) !== oldPolicy) patch.access_policy = parsed
    } catch {
      return
    }
    if (Object.keys(patch).length === 0) {
      notifications.show({
        message: 'Nenhuma alteração detectada.',
        color: 'gray',
      })
      onClose()
      return
    }
    updateMutation.mutate(patch)
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Editar app ${app.slug}`}
      size="lg"
    >
      <Stack gap="md">
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
          Mudanças aplicadas imediatamente. Sessões ativas continuam válidas
          mas próximo request usa novos valores. Campos sensíveis (slug,
          routing_mode) não editáveis — exigem reprovisionar.
        </Alert>

        <TextInput
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.currentTarget.value)}
        />

        <TextInput
          label="Hostname público"
          placeholder="portal.qa.tenant.com.br"
          value={hostname}
          onChange={(e) => setHostname(e.currentTarget.value)}
          error={hostnameError}
          ff="JetBrains Mono, monospace"
          description="FQDN que o usuário digita no browser. Mudar requer DNS + cert válidos."
        />

        {hostnameChanged && !hostnameError ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Stack gap={4}>
              <strong>Atenção: trocar hostname tem 3 pré-requisitos.</strong>
              <span>
                1. <code>DNS Route53 público</code> do novo hostname
                <code> {hostnameTrimmed}</code> precisa apontar pro broker ZTNA
                (CNAME/A → ALB do broker).
              </span>
              <span>
                2. <code>Cert ACM</code> precisa cobrir o novo hostname (wildcard
                <code> *.{hostnameTrimmed.split('.').slice(1).join('.')}</code> normalmente).
              </span>
              <span>
                3. Usuários antigos que acessam pelo hostname atual
                <code> {app.hostname}</code> perdem acesso. Avise-os.
              </span>
            </Stack>
          </Alert>
        ) : null}

        <TextInput
          label="Upstream URL"
          placeholder="https://alb-XXX.us-east-1.elb.amazonaws.com/"
          value={upstreamUrl}
          onChange={(e) => setUpstreamUrl(e.currentTarget.value)}
          error={
            upstreamCircular
              ? `upstream aponta pro próprio hostname (${hostnameTrimmed || app.hostname}) — vai criar loop. Use ALB legacy direto ou IP interno.`
              : null
          }
          ff="JetBrains Mono, monospace"
        />

        <Radio.Group
          label="Identity mode"
          value={identityMode}
          onChange={(v) => setIdentityMode(v as 'header' | 'oidc' | 'mtls')}
        >
          <Stack gap={4} mt={4}>
            <Radio value="header" label="Header (X-Forwarded-User/Email)" />
            <Radio value="oidc" label="OIDC (broker como IdP)" />
            <Radio value="mtls" label="mTLS (cert client por user)" />
          </Stack>
        </Radio.Group>

        {identityMode === 'oidc' ? (
          <TagsInput
            label="OIDC redirect URIs"
            placeholder="https://portal.example.com/oauth/callback"
            value={redirectUris}
            onChange={setRedirectUris}
            description="Allowlist anti-phishing. Press Enter para adicionar."
          />
        ) : null}

        <TimeWindowFieldset
          value={allowedHours}
          bypassRoles={bypassRoles}
          onChange={(v, roles) => {
            setAllowedHours(v)
            setBypassRoles(roles)
          }}
        />

        <Switch
          label="Privado (restrito à organização do app)"
          description="Só funcionários da organização dona do app acessam. Padrão = público."
          checked={isPrivate}
          onChange={(e) => setIsPrivate(e.currentTarget.checked)}
        />

        <Textarea
          label="Access policy avançado (JSON)"
          description="Demais campos: required_permissions, allowed_environments, allowed_cidrs, requires_mfa, requires_managed_device, max_session_minutes. allowed_hours e bypass_time_window_roles são geridos pelo controle acima."
          value={accessPolicyJson}
          onChange={(e) => setAccessPolicyJson(e.currentTarget.value)}
          autosize
          minRows={6}
          error={accessPolicyError ? `JSON inválido: ${accessPolicyError}` : null}
          ff="JetBrains Mono, monospace"
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            color="teal"
            onClick={handleSubmit}
            loading={updateMutation.isPending}
            disabled={upstreamCircular || accessPolicyError != null || hostnameError != null}
          >
            Salvar alterações
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export default AppEditModal
